<template>
  <div class="min-h-screen bg-bg-primary flex theme-transition">
    <!-- Sidebar -->
    <div
      v-if="mobileSidebarOpen"
      class="fixed inset-0 bg-black/60 z-40 md:hidden backdrop-blur-sm transition-opacity duration-300"
      @click="mobileSidebarOpen = false"
    ></div>

    <aside
      ref="sidebarRef"
      :class="[
        'fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 bg-bg-secondary border-r border-border-primary w-64 theme-transition',
        mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        !mobileSidebarOpen && !sidebarOpen && 'md:w-20'
      ]"
    >
      <!-- Logo -->
      <div class="flex items-center h-16 px-6 border-b border-border-primary overflow-hidden">
        <router-link to="/" class="flex items-center">
          <img 
            src="@/assets/logo.png" 
            alt="AttTrack" 
            :class="[
              'h-8 w-auto transition-all duration-300',
              !sidebarOpen && !mobileSidebarOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
            ]"
          />
          <!-- Smaller logo/icon for collapsed state if needed -->
          <div 
            v-if="!sidebarOpen && !mobileSidebarOpen"
            class="absolute left-1/2 -translate-x-1/2 w-10 h-10 bg-accent-primary/10 rounded-xl flex items-center justify-center"
          >
            <span class="text-accent-primary font-bold text-xl">AT</span>
          </div>
        </router-link>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 px-4 py-6 space-y-4 overflow-y-auto">
        <template v-for="section in visibleSections" :key="section.id">
          <!-- Section Header (Only visible when sidebar is expanded) -->
          <div v-if="sidebarOpen || mobileSidebarOpen" class="space-y-2">
            <button 
              @click="toggleSection(section.id)"
              class="flex items-center justify-between w-full px-2 text-xs font-semibold text-text-tertiary uppercase tracking-wider hover:text-text-primary transition-colors cursor-pointer"
            >
              <span class="flex items-center gap-2">
                {{ section.title }}
                <span
                  v-if="section.id === 'leave' && sectionState.leave && leaveRequestBadge > 0"
                  class="min-w-5 h-5 px-1.5 rounded-full bg-error text-white text-[10px] font-bold leading-none normal-case tracking-normal flex items-center justify-center"
                >
                  {{ leaveRequestBadge > 99 ? '99+' : leaveRequestBadge }}
                </span>
              </span>
              <svg 
                :class="['w-3 h-3 transition-transform duration-200', sectionState[section.id] ? 'rotate-180' : '']"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <!-- Section Items -->
            <div v-if="!sectionState[section.id]" class="space-y-1">
              <template v-for="item in section.items" :key="item.to">
                <SidebarItem
                  :to="item.to"
                  :icon="item.icon"
                  :label="item.label"
                  :badge="leaveBadgeFor(item)"
                  :collapsed="false"
                  @click="mobileSidebarOpen = false"
                />
              </template>
            </div>
          </div>

          <!-- Collapsed Sidebar Mode -->
          <div v-else class="space-y-2">
            <div class="h-px bg-border-primary mx-2 my-2 first:hidden"></div>
            <template v-for="item in section.items" :key="item.to">
              <SidebarItem
                :to="item.to"
                :icon="item.icon"
                :label="item.label"
                :badge="leaveBadgeFor(item)"
                :collapsed="true"
                @click="mobileSidebarOpen = false"
              />
            </template>
          </div>
        </template>
      </nav>

      <!-- Sidebar Footer -->
      <div class="border-t border-border-primary p-4">
        <!-- Theme Toggle -->
        <button
          @click="toggleTheme"
          :class="[
            'w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200',
            'hover:bg-bg-tertiary text-text-secondary hover:text-text-primary'
          ]"
        >
          <svg v-if="themeStore.isDarkMode" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
          </svg>
          <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
          </svg>
          <transition name="fade">
            <span v-if="sidebarOpen || mobileSidebarOpen" class="text-sm font-medium">
              {{ themeStore.isDarkMode ? 'Light Mode' : 'Dark Mode' }}
            </span>
          </transition>
        </button>

        <!-- Collapse Toggle (Desktop only) -->
        <button
          @click="sidebarOpen = !sidebarOpen"
          class="hidden md:flex w-full items-center justify-center px-3 py-2.5 mt-2 rounded-xl hover:bg-bg-tertiary text-text-secondary hover:text-text-primary transition-all duration-200"
        >
          <svg
            :class="['w-5 h-5 transition-transform duration-300', sidebarOpen ? '' : 'rotate-180']"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/>
          </svg>
        </button>
      </div>
    </aside>

    <!-- Main Content -->
    <div
      :class="[
        'flex-1 flex flex-col transition-all duration-300 min-w-0',
        sidebarOpen ? 'md:ml-64' : 'md:ml-20'
      ]"
    >
      <!-- Top Bar -->
      <header class="h-16 bg-bg-secondary border-b border-border-primary flex items-center justify-between px-4 sm:px-6 theme-transition">
        <div class="flex items-center space-x-4">
          <!-- Mobile Menu Button -->
          <button
            ref="mobileMenuBtnRef"
            @click="mobileSidebarOpen = !mobileSidebarOpen"
            class="md:hidden p-2 hover:bg-bg-tertiary rounded-xl text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <!-- Search Bar -->
          <!-- Spacer -->
          <div class="hidden sm:block flex-1 max-w-xl"></div>
        </div>

        <!-- Right Section -->
        <div class="flex items-center space-x-2 sm:space-x-4">
          <!-- Tutorial / Help Button -->
          <button 
            v-if="!authStore.isSuperAdmin"
            @click="handleHelp"
            class="p-2 hover:bg-bg-tertiary rounded-xl text-text-secondary hover:text-accent-primary transition-colors flex items-center space-x-1"
            title="Help & Tutorial"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span class="hidden sm:inline text-sm font-medium">Help</span>
          </button>

          <!-- User Menu -->
          <div class="relative" ref="userMenuRef">
            <button
              @click="userMenuOpen = !userMenuOpen"
              class="flex items-center space-x-2 sm:space-x-3 p-1.5 sm:p-2 hover:bg-bg-tertiary rounded-xl transition-colors"
            >
              <div class="w-8 h-8 bg-accent-primary/10 rounded-lg flex items-center justify-center">
                <span class="text-sm font-semibold text-accent-primary">
                  {{ authStore.userProfile?.name?.charAt(0)?.toUpperCase() || 'U' }}
                </span>
              </div>
              <div class="text-left hidden lg:block">
                <p class="text-sm font-medium text-text-primary">{{ authStore.userProfile?.name?.split(' ')[0] || 'User' }}</p>
                <p class="text-xs text-text-secondary">{{ getRoleLabel(authStore.userProfile?.role, authStore.customRoles) }}</p>
              </div>
              <svg class="w-4 h-4 text-text-secondary hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>

            <!-- Dropdown Menu -->
            <transition name="dropdown">
              <div
                v-if="userMenuOpen"
                class="absolute right-0 mt-2 w-56 bg-bg-primary rounded-xl border border-border-primary shadow-theme-lg py-2 z-50 theme-transition"
              >
                <router-link
                  to="/profile"
                  class="flex items-center px-4 py-2.5 text-sm text-text-primary hover:bg-bg-secondary transition-colors"
                  @click="userMenuOpen = false"
                >
                  <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7 7z"/>
                  </svg>
                  My Profile
                </router-link>
                <div class="border-t border-border-primary my-2"></div>
                <button
                  @click="handleLogout"
                  class="flex items-center w-full px-4 py-2.5 text-sm text-error hover:bg-error/10 transition-colors"
                >
                  <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                  </svg>
                  Logout
                </button>
              </div>
            </transition>
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="flex-1 overflow-y-auto">
        <slot></slot>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useCompanyStore } from '@/stores/company'
import { formatFirebaseError } from '@/utils/errorHandlers'
import SidebarItem from './SidebarItem.vue'
import { MENU_SECTIONS } from '@/config/menu'
import { allowsPageAction, canEditPayrollRules, canFetchSalesProfit, getRoleLabel, pageAccessMode } from '@/utils/rbac'
import { pendingLeaveRequestCount } from '@/composables/useLeaveRequestAlerts'

const router = useRouter()
const toast = useToast()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const companyStore = useCompanyStore()

const sidebarOpen = ref(true)
const mobileSidebarOpen = ref(false)
const userMenuOpen = ref(false)
const userMenuRef = ref(null)
const sidebarRef = ref(null)
const mobileMenuBtnRef = ref(null)

// Section State (true = collapsed, false = expanded)
// Default to some expanded
const sectionState = ref({
  overview: false,
  attendance: false,
  workforce: false,
  scheduling: false,
  leave: false,
  organization: false,
  system: false
})

const toggleSection = (id) => {
  sectionState.value[id] = !sectionState.value[id]
}

const isItemVisible = (item) => {
  // Check login status first
  if (!authStore.userProfile) return false
  
  const isAdmin = authStore.isSuperAdmin
  const isMgr = authStore.isManager
  
  // Role-based exclusions
  if (item.requireNotSuperAdmin && isAdmin) return false
  if (item.requireNotManager && isMgr) return false
  if (item.requiredEmail && String(authStore.userProfile?.email || authStore.user?.email || '').trim().toLowerCase() !== item.requiredEmail) return false
  
  // Permission check
  if (item.payrollRules) {
    return canEditPayrollRules({
      email: authStore.userProfile?.email || authStore.user?.email,
      role: authStore.userProfile?.role,
    }, authStore.customRoles)
  }
  if (item.salesProfit) {
    return canFetchSalesProfit({
      email: authStore.userProfile?.email || authStore.user?.email,
      role: authStore.userProfile?.role,
    }, authStore.customRoles)
  }
  if (item.pageId) {
    const mode = pageAccessMode(authStore.userProfile.role, item.pageId, authStore.customRoles)
    if (mode) return allowsPageAction(mode, 'retrieve')
  }
  if (item.permission && !authStore.can(item.permission)) return false
  
  return true
}

const canSeeLeaveBadge = computed(() => {
  return authStore.isManager || authStore.userProfile?.canManageLeaveRequests === true
})

const leaveRequestBadge = computed(() => {
  return canSeeLeaveBadge.value ? pendingLeaveRequestCount.value : 0
})

const leaveBadgeFor = (item) => {
  if (item.to !== '/leave-request' && item.to !== '/leave-management') return 0
  return leaveRequestBadge.value
}

const visibleSections = computed(() => {
  if (!authStore.userProfile) return []

  return MENU_SECTIONS
    .map((section) => {
      const visibleItems = section.items.filter((item) => isItemVisible(item))
      if (visibleItems.length === 0) return null
      return {
        ...section,
        items: visibleItems,
      }
    })
    .filter(Boolean)
})

const toggleTheme = () => {
  themeStore.toggleTheme()
}

const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/login')
    toast.success('Logged out successfully')
  } catch (error) {
    toast.error(formatFirebaseError(error))
  }
  userMenuOpen.value = false
}

const handleHelp = () => {
  if (authStore.isManager) {
    router.push('/employer-onboarding')
  } else {
    router.push('/onboarding')
  }
}

// Close menus when clicking outside
const handleClickOutside = (event) => {
  // Handle User Menu
  if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    userMenuOpen.value = false
  }

  // Handle Mobile Sidebar
  if (
    mobileSidebarOpen.value &&
    sidebarRef.value &&
    !sidebarRef.value.contains(event.target) &&
    mobileMenuBtnRef.value &&
    !mobileMenuBtnRef.value.contains(event.target)
  ) {
    mobileSidebarOpen.value = false
  }
}

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  
  // Load custom roles if authenticated and have companyId
  if (authStore.isAuthenticated && authStore.userProfile?.companyId && !companyStore.rolesLoaded) {
    try {
      await companyStore.loadCustomRoles()
    } catch (error) {
      console.warn('Failed to load custom roles in layout:', error)
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
